/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SUBSCRIPTION PURCHASE SERVICE - Purchase Validation & Warnings
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * This service handles subscription purchase validations and warnings.
 * It checks for upgrade/downgrade scenarios and warns users about:
 * - Cancelling existing subscription
 * - Losing remaining days
 * - Feature changes (gains/losses)
 * - Product/domain limit changes
 * 
 * Similar to planPurchaseService but for subscriptions.
 * 
 * @author MyTamilKart Development Team
 * @version 1.0
 */

import api from '../api/axiosInstance';
import subscriptionService from './subscriptionService';
import { countOwnProducts } from '../utils/productCount';

// ═══════════════════════════════════════════════════════════════════════════
// PLAN HIERARCHY - For upgrade/downgrade detection
// ═══════════════════════════════════════════════════════════════════════════
// This maps plan names to hierarchy levels for upgrade/downgrade detection
// Plans not in this list will be treated as level 0 (lowest)
const SUBSCRIPTION_HIERARCHY = {
  'Starter': 1,
  'starter': 1,
  'Pro': 2,
  'pro': 2,
  'Premium': 3,
  'premium': 3,
  'testing plan': 4,
  'business': 4,
  'Enterprise': 5,
  'enterprise': 5
};

/**
 * Get plan hierarchy level
 * Handles case-insensitive matching and returns 0 for unknown plans
 */
function getPlanLevel(planName) {
  if (!planName) return 0;
  return SUBSCRIPTION_HIERARCHY[planName] || SUBSCRIPTION_HIERARCHY[planName.toLowerCase()] || 0;
}

const subscriptionPurchaseService = {

  /**
   * Validate subscription purchase before proceeding
   * ------------------------------------------
   * Checks current subscription and validates the purchase scenario.
   * 
   * @param {string} newPlanId - Razorpay plan ID user wants to subscribe to
   * @param {string} newPlanName - Plan name (Starter/Pro/Premium)
   * @param {Object} currentSubscriptionInfo - Current subscription information
   * @param {number} currentProductCount - Current number of products
   * @param {number} currentDomainCount - Current number of domains
   * @returns {Object} Validation result with warnings
   * 
   * RETURNS:
   * {
   *   allowed: boolean,
   *   scenario: string ('new'|'renewal'|'upgrade'|'downgrade'|'blocked'),
   *   warnings: string[],
   *   blockers: string[],
   *   confirmationRequired: boolean,
   *   message: string,
   *   gains: string[],
   *   losses: string[],
   *   daysLost: number
   * }
   */
  async validatePurchase(newPlanId, newPlanName, currentSubscriptionInfo, currentProductCount = 0, currentDomainCount = 0) {
    const result = {
      allowed: true,
      scenario: 'new',
      warnings: [],
      blockers: [],
      confirmationRequired: false,
      message: '',
      gains: [],
      losses: [],
      daysLost: 0
    };

    // SCENARIO 1: No current subscription (new purchase)
    if (!currentSubscriptionInfo || !currentSubscriptionInfo.has_subscription || currentSubscriptionInfo.status !== 'active') {
      result.scenario = 'new';
      result.message = `You're subscribing to ${newPlanName} plan. You'll get full access immediately with auto-renewal.`;
      return result;
    }

    const currentPlanName = currentSubscriptionInfo.plan_name;
    const currentPlanId = currentSubscriptionInfo.plan_id;
    const currentLevel = getPlanLevel(currentPlanName);
    const newLevel = getPlanLevel(newPlanName);
    
    console.log('[SUBSCRIPTION PURCHASE] Plan comparison:', {
      current: { name: currentPlanName, id: currentPlanId, level: currentLevel },
      new: { name: newPlanName, id: newPlanId, level: newLevel }
    });

    // Calculate days remaining on current subscription
    let daysRemaining = 0;
    if (currentSubscriptionInfo.next_billing_date) {
      const nextBilling = new Date(currentSubscriptionInfo.next_billing_date);
      const today = new Date();
      daysRemaining = Math.ceil((nextBilling - today) / (1000 * 60 * 60 * 24));
    }

    // SCENARIO 2: Same plan (already subscribed)
    if (currentPlanId === newPlanId || currentPlanName === newPlanName) {
      result.scenario = 'renewal';
      result.allowed = false;
      result.blockers.push(`❌ You already have an active ${currentPlanName} subscription.`);
      result.message = `You're already subscribed to ${currentPlanName}. Your subscription will auto-renew on ${currentSubscriptionInfo.next_billing_date || 'next billing date'}.`;
      
      if (daysRemaining > 0) {
        result.warnings.push(`ℹ️ Your current subscription has ${daysRemaining} days remaining.`);
      }
      
      return result;
    }

    // SCENARIO 3: Upgrade (moving to higher plan)
    if (newLevel > currentLevel) {
      result.scenario = 'upgrade';
      result.confirmationRequired = true;
      result.message = `⬆️ Upgrading from ${currentPlanName} to ${newPlanName}`;
      
      // Warn about cancellation and lost days
      result.warnings.push(`⚠️ Your current ${currentPlanName} subscription will be CANCELLED immediately.`);
      
      if (daysRemaining > 0) {
        result.warnings.push(`⚠️ You will LOSE ${daysRemaining} days remaining on your ${currentPlanName} subscription (NO REFUND).`);
        result.daysLost = daysRemaining;
      }
      
      result.warnings.push(`✅ Your new ${newPlanName} subscription will start immediately after payment.`);
      
      // Get plan limits
      const newLimits = await this.getPlanLimits(newPlanId, newPlanName);
      const currentLimits = currentSubscriptionInfo.limits || {};
      
      // Show what they'll gain
      const gains = [];
      
      if (newLimits.maxProducts > this.getLimitValue(currentLimits, 'max_products')) {
        gains.push(`✅ Products: ${this.getLimitValue(currentLimits, 'max_products')} → ${newLimits.maxProducts === -1 ? 'Unlimited' : newLimits.maxProducts}`);
      }
      
      if (newLimits.maxDomains > this.getLimitValue(currentLimits, 'max_domains')) {
        gains.push(`✅ Domains: ${this.getLimitValue(currentLimits, 'max_domains')} → ${newLimits.maxDomains === -1 ? 'Unlimited' : newLimits.maxDomains}`);
      }
      
      if (newLimits.analytics !== this.getLimitValue(currentLimits, 'analytics')) {
        gains.push(`✅ Analytics: ${this.getLimitValue(currentLimits, 'analytics')} → ${newLimits.analytics}`);
      }
      
      if (newLimits.discountCodes && !this.getLimitValue(currentLimits, 'discount_codes')) {
        gains.push(`✅ Discount codes enabled`);
      }
      
      if (newLimits.multiStore && !this.getLimitValue(currentLimits, 'multi_store')) {
        gains.push(`✅ Multi-store management enabled`);
      }
      
      result.gains = gains;
      return result;
    }

    // SCENARIO 4: Downgrade (moving to lower plan) ⚠️
    if (newLevel < currentLevel && newLevel > 0) {
      result.scenario = 'downgrade';
      result.confirmationRequired = true;
      result.message = `⬇️ Downgrading from ${currentPlanName} to ${newPlanName}`;
      
      // Warn about cancellation and lost days
      result.warnings.push(`⚠️ Your current ${currentPlanName} subscription will be CANCELLED immediately.`);
      
      if (daysRemaining > 0) {
        result.warnings.push(`⚠️ You will LOSE ${daysRemaining} days remaining on your ${currentPlanName} subscription (NO REFUND).`);
        result.daysLost = daysRemaining;
      }
      
      // Get plan limits
      const newLimits = await this.getPlanLimits(newPlanId, newPlanName);
      const currentLimits = currentSubscriptionInfo.limits || {};
      
      // Check product limit
      if (currentProductCount > newLimits.maxProducts) {
        result.allowed = false;
        result.blockers.push(
          `❌ BLOCKED: You have ${currentProductCount} products but ${newPlanName} allows only ${newLimits.maxProducts}.`
        );
        result.blockers.push(
          `   Action required: Delete ${currentProductCount - newLimits.maxProducts} products before downgrading.`
        );
      } else if (currentProductCount > 0) {
        result.warnings.push(
          `⚠️ Product limit will reduce: ${this.getLimitValue(currentLimits, 'max_products')} → ${newLimits.maxProducts} (You have ${currentProductCount})`
        );
      }
      
      // Check domain limit
      if (newLimits.maxDomains !== -1 && currentDomainCount > newLimits.maxDomains) {
        result.allowed = false;
        result.blockers.push(
          `❌ BLOCKED: You have ${currentDomainCount} domains but ${newPlanName} allows only ${newLimits.maxDomains}.`
        );
        result.blockers.push(
          `   Action required: Remove ${currentDomainCount - newLimits.maxDomains} custom domains before downgrading.`
        );
      }
      
      // Warn about lost features
      const losses = [];
      
      if (this.getLimitValue(currentLimits, 'discount_codes') && !newLimits.discountCodes) {
        losses.push(`❌ Discount codes will be disabled`);
      }
      
      if (this.getLimitValue(currentLimits, 'analytics') === 'Premium Analytics' && newLimits.analytics !== 'Premium Analytics') {
        losses.push(`❌ Premium analytics will be lost`);
      }
      
      if (this.getLimitValue(currentLimits, 'analytics') === 'Advanced Analytics' && newLimits.analytics === 'basic') {
        losses.push(`❌ Advanced analytics will be lost`);
      }
      
      if (this.getLimitValue(currentLimits, 'multi_store') && !newLimits.multiStore) {
        losses.push(`❌ Multi-store management will be disabled`);
      }
      
      result.losses = losses;
      
      if (result.allowed) {
        result.warnings.push(`✅ Your new ${newPlanName} subscription will start immediately after payment.`);
      }
      
      return result;
    }
    
    // SCENARIO 5: Unknown plan change (both plans not in hierarchy)
    // This handles newly added custom plans that aren't in SUBSCRIPTION_HIERARCHY
    if (currentLevel === 0 || newLevel === 0) {
      result.scenario = 'change';
      result.confirmationRequired = true;
      result.message = `🔄 Changing subscription from ${currentPlanName} to ${newPlanName}`;
      
      // Warn about cancellation and lost days
      result.warnings.push(`⚠️ Your current ${currentPlanName} subscription will be CANCELLED immediately.`);
      
      if (daysRemaining > 0) {
        result.warnings.push(`⚠️ You will LOSE ${daysRemaining} days remaining on your ${currentPlanName} subscription (NO REFUND).`);
        result.daysLost = daysRemaining;
      }
      
      result.warnings.push(`✅ Your new ${newPlanName} subscription will start immediately after payment.`);
      
      // Try to get plan limits to show comparison
      try {
        const newLimits = await this.getPlanLimits(newPlanId, newPlanName);
        const currentLimits = currentSubscriptionInfo.limits || {};
        
        // Show feature comparison if available
        const changes = [];
        
        if (newLimits.maxProducts !== this.getLimitValue(currentLimits, 'max_products')) {
          changes.push(`📦 Products: ${this.getLimitValue(currentLimits, 'max_products')} → ${newLimits.maxProducts === -1 ? 'Unlimited' : newLimits.maxProducts}`);
        }
        
        if (newLimits.maxDomains !== this.getLimitValue(currentLimits, 'max_domains')) {
          changes.push(`🌐 Domains: ${this.getLimitValue(currentLimits, 'max_domains')} → ${newLimits.maxDomains === -1 ? 'Unlimited' : newLimits.maxDomains}`);
        }
        
        if (changes.length > 0) {
          result.warnings.push(`\nPlan changes:`);
          result.warnings.push(...changes);
        }
      } catch (error) {
        console.error('[SUBSCRIPTION PURCHASE] Error getting plan limits for comparison:', error);
      }
      
      return result;
    }

    return result;
  },

  /**
   * Get plan limits from API
   * -------------------------
   * Fetches plan limits from subscription plans API.
   * 
   * @param {string} planId - Razorpay plan ID, or Plan Management plan ID
   * @param {string} planName - Plan name (fallback)
   * @returns {Promise<Object>} Plan limits
   */
  async getPlanLimits(planId, planName) {
    try {
      const response = await api.get('/subscription/plans/list');
      
      if (response.data.status === 'success' && response.data.result?.plans) {
        // planId is the Razorpay plan ID, or the Plan Management plan ID before its first subscription
        const plan = response.data.result.plans.find(p => p.razorpay_plan_id === planId || p.plan_id === planId);
        
        // Plan Management plans carry no limits list; those use the defaults for the plan name
        if (plan && plan.limits && plan.limits.length > 0) {
          // Convert API limits to usable format
          const limits = {
            maxProducts: this.getLimitValue(plan.limits, 'max_products'),
            maxDomains: this.getLimitValue(plan.limits, 'max_domains'),
            analytics: this.getLimitValue(plan.limits, 'analytics'),
            discountCodes: this.getLimitValue(plan.limits, 'discount_codes'),
            multiStore: this.getLimitValue(plan.limits, 'multi_store'),
            support: this.getLimitValue(plan.limits, 'support')
          };
          
          return limits;
        }
      }
      
      // Fallback to default limits based on plan name
      return this.getDefaultLimits(planName);
    } catch (error) {
      console.error('[SUBSCRIPTION PURCHASE] Error fetching plan limits:', error);
      return this.getDefaultLimits(planName);
    }
  },

  /**
   * Get limit value from limits array
   * ----------------------------------
   * Extracts value from API limits array format.
   * 
   * @param {Array|Object} limits - Limits array or object
   * @param {string} key - Limit key to find
   * @returns {any} Limit value
   */
  getLimitValue(limits, key) {
    if (Array.isArray(limits)) {
      const limit = limits.find(l => l.key === key);
      if (limit) {
        // Handle special cases
        if (key === 'max_domains' && (limit.value === 'Unlimited Domains' || limit.value === 'unlimited')) {
          return -1;
        }
        if (key === 'multi_store' && limit.value === 'Multi-store Management') {
          return true;
        }
        return limit.value;
      }
    } else if (typeof limits === 'object') {
      // Handle object format (from subscriptionService)
      const keyMap = {
        'max_products': 'maxProducts',
        'max_domains': 'maxDomains',
        'analytics': 'analytics',
        'discount_codes': 'discountCodes',
        'multi_store': 'multiStore',
        'support': 'support'
      };
      return limits[keyMap[key]] || limits[key];
    }
    return null;
  },

  /**
   * Get default limits (fallback)
   * ------------------------------
   * Returns default limits when API fails.
   * 
   * @param {string} planName - Plan name
   * @returns {Object} Default limits
   */
  getDefaultLimits(planName) {
    const defaults = {
      'Starter': {
        maxProducts: 50,
        maxDomains: 1,
        analytics: 'basic',
        discountCodes: false,
        multiStore: false,
        support: 'email'
      },
      'Pro': {
        maxProducts: 150,
        maxDomains: 1,
        analytics: 'Advanced Analytics',
        discountCodes: true,
        multiStore: false,
        support: 'Priority Support'
      },
      'Premium': {
        maxProducts: 500,
        maxDomains: -1,
        analytics: 'Premium Analytics',
        discountCodes: true,
        multiStore: true,
        support: '24/7 Priority Support'
      }
    };
    
    if (!defaults[planName]) {
      // Plan Management plans carry no limits list, so limits follow the plan name
      console.warn(`[SUBSCRIPTION PURCHASE] No limits defined for plan "${planName}"; using Starter limits. Name plans Starter, Pro or Premium in Plan Management.`);
    }
    return defaults[planName] || defaults['Starter'];
  },

  /**
   * Get current product count
   * --------------------------
   * Fetches the current number of products for validation.
   * 
   * @param {string} businessId - Business user ID
   * @returns {Promise<number>} Product count
   */
  async getCurrentProductCount(businessId) {
    try {
      const response = await api.get('/gift/list', { params: { business_id: businessId } });
      if (response.data.status === 'success') {
        // The untouched sample product doesn't take a plan slot (same as the API)
        return countOwnProducts(response.data.result);
      }
      return 0;
    } catch (error) {
      console.error('[SUBSCRIPTION PURCHASE] Error fetching product count:', error);
      return 0;
    }
  },

  /**
   * Get current domain count
   * -------------------------
   * Fetches the current number of domains for validation.
   * 
   * @param {string} businessId - Business user ID
   * @returns {Promise<number>} Domain count
   */
  async getCurrentDomainCount(businessId) {
    try {
      const response = await api.get(`/business/domain/info?user_id=${businessId}`);
      if (response.data.status === 'success' && response.data.result) {
        return response.data.result.custom_domain ? 1 : 0;
      }
      return 0;
    } catch (error) {
      console.error('[SUBSCRIPTION PURCHASE] Error fetching domain count:', error);
      return 0;
    }
  },

  /**
   * Format validation result for display
   * -------------------------------------
   * Converts validation result into user-friendly messages.
   * 
   * @param {Object} validation - Validation result
   * @returns {Object} Formatted messages for UI
   */
  formatValidationMessages(validation) {
    const messages = {
      title: '',
      description: '',
      warnings: validation.warnings || [],
      blockers: validation.blockers || [],
      gains: validation.gains || [],
      losses: validation.losses || [],
      canProceed: validation.allowed,
      needsConfirmation: validation.confirmationRequired,
      daysLost: validation.daysLost || 0
    };

    switch (validation.scenario) {
      case 'renewal':
        messages.title = '🔄 Already Subscribed';
        messages.description = validation.message;
        break;
      
      case 'upgrade':
        messages.title = '⬆️ Subscription Upgrade';
        messages.description = validation.message;
        break;
      
      case 'downgrade':
        messages.title = '⬇️ Subscription Downgrade';
        messages.description = validation.message;
        break;
      
      case 'new':
        messages.title = '🎉 New Subscription';
        messages.description = validation.message;
        break;
      
      case 'blocked':
        messages.title = '❌ Cannot Change Subscription';
        messages.description = validation.message;
        break;
    }

    return messages;
  }
};

export default subscriptionPurchaseService;
